import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { trackHomeView, trackSignUp } from '../lib/analytics';
import { ApiError } from '../lib/api';
import { fetchPublicConfig } from '../lib/config';
import {
  loadGisScript,
  signInWithGoogleIdToken,
} from '../lib/googleSignIn';
import { fetchMe } from '../lib/me';
import './Home.css';

type State =
  | { kind: 'probing' }
  | { kind: 'ready'; clientId: string }
  | { kind: 'signing-in' }
  | { kind: 'error'; message: string };

const POST_SIGNIN_REDIRECT = '/stories';

const SHOWCASE_BASE = 'https://storage.googleapis.com/kinotale-media/landing/showcase';
const SHOWCASE_COVERS = [
  { src: `${SHOWCASE_BASE}/luna-moonlit-meadow.jpg`, alt: 'Watercolor bunny in a moonlit meadow' },
  { src: `${SHOWCASE_BASE}/theo-cloud-train.jpg`, alt: 'Boy riding a magical train of clouds' },
  { src: `${SHOWCASE_BASE}/maya-paper-dragon.jpg`, alt: 'Girl with a glowing origami dragon at sunset' },
] as const;

export function Home() {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'probing' });
  const buttonSlot = useRef<HTMLDivElement>(null);

  // If the user is already authenticated, go straight to the app.
  useEffect(() => {
    trackHomeView();
    let cancelled = false;
    fetchMe()
      .then(() => {
        if (!cancelled) window.location.replace(POST_SIGNIN_REDIRECT);
      })
      .catch(async (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          try {
            const config = await fetchPublicConfig();
            if (cancelled) return;
            if (!config.google_client_id) {
              setState({
                kind: 'error',
                message: t('home.signInNotConfigured'),
              });
              return;
            }
            setState({ kind: 'ready', clientId: config.google_client_id });
          } catch (configErr) {
            if (cancelled) return;
            setState({
              kind: 'error',
              message:
                configErr instanceof Error ? configErr.message : t('home.configurationError'),
            });
          }
          return;
        }
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : t('common.unknownError'),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Render the Google sign-in button once config is ready.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    const { clientId } = state;
    let cancelled = false;

    loadGisScript(i18n.language)
      .then(() => {
        if (cancelled || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          auto_select: true,
          itp_support: true,
          context: 'signin',
          callback: async (response) => {
            setState({ kind: 'signing-in' });
            try {
              await signInWithGoogleIdToken({
                idToken: response.credential,
                clientId,
              });
              trackSignUp('google');
              window.location.href = POST_SIGNIN_REDIRECT;
            } catch (err) {
              let message = t('home.signInFailed');
              if (err instanceof ApiError) {
                const body = err.body as { errors?: { message?: string }[] } | null;
                message = body?.errors?.[0]?.message ?? message;
              } else if (err instanceof Error) {
                message = err.message;
              }
              setState({ kind: 'error', message });
            }
          },
        });
        if (buttonSlot.current) {
          window.google.accounts.id.renderButton(buttonSlot.current, {
            type: 'standard',
            shape: 'pill',
            theme: 'outline',
            text: 'continue_with',
            size: 'large',
            logo_alignment: 'left',
          });
          window.google.accounts.id.prompt();
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setState({ kind: 'error', message: err.message });
      });

    return () => {
      cancelled = true;
    };
  }, [state, t]);

  return (
    <div className="login-page">
      <div className="login-page__stars" aria-hidden="true" />
      <div className="login-page__glow login-page__glow--a" aria-hidden="true" />
      <div className="login-page__glow login-page__glow--b" aria-hidden="true" />

      <main className="login-shell">
        <section className="login-card">
          <img
            src="/logo.png"
            alt="Kinotale"
            className="login-card__logo"
          />
          <h1 className="login-card__title">
            <Trans
              i18nKey="home.title"
              components={[<span className="login-card__accent" key="0" />]}
            />
          </h1>
          <p className="login-card__lede">{t('home.lede')}</p>

          <div className="login-card__signin">
            <div ref={buttonSlot} className="g-signin-slot" />
            <p
              id="status-message"
              className={
                state.kind === 'error'
                  ? 'login-card__status login-card__status--error'
                  : 'login-card__status'
              }
            >
              {messageFor(state, t)}
            </p>
          </div>

          <p className="login-card__fineprint">{t('home.fineprint')}</p>
        </section>

        <aside className="login-showcase" aria-hidden="true">
          {SHOWCASE_COVERS.map((cover, idx) => (
            <figure
              key={cover.src}
              className={`login-showcase__cover login-showcase__cover--${idx + 1}`}
            >
              <img src={cover.src} alt={cover.alt} loading="lazy" decoding="async" />
            </figure>
          ))}
          <div className="login-showcase__sparkle login-showcase__sparkle--a" />
          <div className="login-showcase__sparkle login-showcase__sparkle--b" />
          <div className="login-showcase__sparkle login-showcase__sparkle--c" />
        </aside>
      </main>
    </div>
  );
}

function messageFor(state: State, t: (key: string) => string): string {
  switch (state.kind) {
    case 'probing':
      return t('common.loading');
    case 'signing-in':
      return t('home.signingIn');
    case 'error':
      return state.message;
    case 'ready':
      return '';
  }
}
